/**
 * ¿Qué inspección admite esta reserva, ahora?
 *
 * POR QUÉ EXISTE. Ni la pantalla de entrega ni la de devolución miraban el
 * estado de la renta. Las dos tomaban el `bookingId` de la URL y dibujaban el
 * formulario entero: quince fotos en vivo, la licencia, dos firmas, el odómetro
 * — sobre una reserva cancelada, sobre una ya terminada, o sobre una que
 * todavía no se pagó. Recién al enviar, después de subir decenas de megas desde
 * la calle, el servidor decía que no. Diez minutos de trabajo de dos personas
 * para tirarlos.
 *
 * Y peor en la devolución: para llegar a `completed` la base exige que exista la
 * inspección de entrega (`validate_rental_status_transition`). Sin esa regla
 * dicha ANTES, alguien puede llenar la devolución completa de una renta que
 * nunca pasó por la entrega y estrellarse al final, sin entender por qué.
 *
 * UNA SOLA DERIVACIÓN, NO DOS. Este archivo existe porque el defecto que más
 * veces se repitió en este repo es tener la misma regla escrita en dos lados y
 * arreglar solo una. Las dos pantallas preguntan acá.
 *
 * FALLA ABIERTO, y eso es deliberado: solo se bloquea sobre estados que
 * CONOCEMOS y que hacen la inspección imposible o inútil. Cualquier estado que
 * no reconozcamos deja pasar, porque esto se usa parado junto a un carro y una
 * pantalla que se equivoca cerrando le arruina el día a alguien que sí podía.
 */
import type { InspectionType } from '../types/inspection.types'

export type EtapaInspeccion =
  /** Adelante. */
  | 'permitida'
  /** El carro ya se entregó: lo que toca es la devolución, no la entrega. */
  | 'ya_ocurrio'
  /** Falta un paso previo (pagar, o hacer la entrega antes de devolver). */
  | 'todavia_no'
  /** El viaje ya terminó. */
  | 'terminada'
  /** La reserva se canceló. */
  | 'cancelada'

/**
 * @param estado `rentals.status` tal como viene de la base. Se acepta `string`
 * a propósito: si mañana aparece un estado nuevo, esta función lo deja pasar en
 * vez de romper una entrega real.
 */
export function etapaDeInspeccion(
  estado: string | null | undefined,
  tipo: InspectionType,
): EtapaInspeccion {
  if (!estado) return 'permitida'

  if (estado === 'cancelled') return 'cancelada'
  if (estado === 'completed') return 'terminada'
  if (estado === 'pending_payment') return 'todavia_no'

  if (tipo === 'checkin') {
    // El carro ya está en la calle: llenar otra entrega no aporta evidencia,
    // confunde, y la base rechazaría el salto de estado igual.
    if (estado === 'active' || estado === 'pending_return') return 'ya_ocurrio'
    return 'permitida'
  }

  // Devolución: sin entrega previa no hay nada que devolver, y la máquina de
  // estados de la base tampoco lo permitiría.
  if (estado === 'confirmed' || estado === 'pending_pickup') return 'todavia_no'
  return 'permitida'
}

/** La llave i18n del título para una etapa que no deja seguir. */
export function tituloDeEtapa(etapa: EtapaInspeccion, tipo: InspectionType): string {
  switch (etapa) {
    case 'cancelada':
      return 'inspection.stage.cancelled_title'
    case 'terminada':
      return 'inspection.stage.completed_title'
    case 'ya_ocurrio':
      return 'inspection.stage.already_handed_over_title'
    case 'todavia_no':
      return tipo === 'checkin'
        ? 'inspection.stage.not_paid_title'
        : 'inspection.stage.checkin_first_title'
    default:
      return ''
  }
}

/** La llave i18n del cuerpo para una etapa que no deja seguir. */
export function cuerpoDeEtapa(etapa: EtapaInspeccion, tipo: InspectionType): string {
  switch (etapa) {
    case 'cancelada':
      return 'inspection.stage.cancelled_body'
    case 'terminada':
      return 'inspection.stage.completed_body'
    case 'ya_ocurrio':
      return 'inspection.stage.already_handed_over_body'
    case 'todavia_no':
      return tipo === 'checkin'
        ? 'inspection.stage.not_paid_body'
        : 'inspection.stage.checkin_first_body'
    default:
      return ''
  }
}
